import {
  IInterior,
  IOrder,
  IVarnish,
  IVehicleConfiguration,
  IConfiguration,
  INextArticle,
  IPimSnapshot,
  isSkipCrystallize,
  MAKE,
  IMargin,
  findCorrectVatCode,
} from 'next-common';
import { IEquipmentIdAndSKU } from 'sales-crystallize-common';
import { cloneDeep, defaultTo, isEmpty, isEqual, sortBy } from 'lodash';
import { IModelPackages, IVehicleModel } from '../../components/sales/sales-vehicle-picker-page';
import {
  addInitialAccessories,
  addInitialEquipment,
  addInitialPcodes,
  addInitialVehicleTyre,
  calculateRetailPrice,
  moveRecommendedOnTop,
  prepareDeliveryChargeInput,
  prepareVehicleConfig,
  prepareVehicleModel,
  prepareVehicleVariant,
  removeNullOrUndefindValuesFromArray,
  VehicleConfig,
} from './vehicleOrder';
import { CartProps, CartSummaryData } from 'types';
import { AccessoryTool } from '../../components/sales/sales-vehicle-accessories-page/AccessoryTool';
import { ArticleType } from 'sales-types-common';
import { DEFAULT_TAX, IOverriddenTaxAndUsageCode } from '../../common';

export const getCurrentModelData = (modelId: string, vehicleData: IVehicleModel[]) => {
  const currentModelData: IVehicleModel[] = vehicleData?.filter((vehicle) => vehicle?.id === modelId);
  const clonedCurrentModelData = cloneDeep(currentModelData);
  const reOrderPackages: IModelPackages[] = moveRecommendedOnTop<IModelPackages>(
    clonedCurrentModelData?.[0]?.salesPackages
  );
  if (clonedCurrentModelData?.[0]?.salesPackages) clonedCurrentModelData[0].salesPackages = reOrderPackages;
  return clonedCurrentModelData;
};

export const getCurrentVariantData = (variantId: string, modelData) => {
  const currentVariantData: IModelPackages[] = modelData?.salesPackages?.filter((model) => model?.id === variantId);
  const clonedCurrentVariantData = cloneDeep(currentVariantData);
  const { colors, interiors, rims, selectableEquipment } = clonedCurrentVariantData?.[0] || {};
  const colorSorted = sortBy(colors, 'additionalPriceExcludingVat').reverse();
  const rimsSorted = sortBy(rims, 'priceExcludingVat').reverse();
  const selectableEquipmentSorted = sortBy(selectableEquipment, 'priceExcludingVat').reverse();
  const interiorsSorted = sortBy(interiors, 'additionalPriceExcludingVat').reverse();
  const reOrderColors: IVarnish[] = moveRecommendedOnTop<IVarnish>(colorSorted);
  const reOrderInterior: IInterior[] = moveRecommendedOnTop<IInterior>(interiorsSorted);

  if (rims) clonedCurrentVariantData[0].rims = rimsSorted;
  if (interiors) clonedCurrentVariantData[0].selectableEquipment = selectableEquipmentSorted;
  if (colors) clonedCurrentVariantData[0].colors = reOrderColors;
  if (interiors) clonedCurrentVariantData[0].interiors = reOrderInterior;
  return clonedCurrentVariantData;
};

export const getMappedEquipement = (
  modelId: string,
  variantId: string,
  vehicleData: IVehicleModel[]
): IEquipmentIdAndSKU[] => {
  const currentModelData: IVehicleModel[] = getCurrentModelData(modelId, vehicleData);
  const currentVariantData: IModelPackages[] = getCurrentVariantData(variantId, currentModelData?.[0]);
  return currentVariantData?.[0]?.mappedEquipment ?? [];
};

//Update dynamically changed prices of selectables in cart
export const updateCartDataPrices = (
  cartData: CartProps,
  setCartData: (data: CartProps) => void,
  disableCartSkus: string[],
  priceUpdate,
  selectablePrices: { string: {} } = null
) => {
  const existingCartData = cloneDeep(cartData);
  let salesTotalExcludingVatAndTax = existingCartData?.prices?.salesTotalExcludingVatAndTax;

  if (disableCartSkus?.length) {
    //disabled until testing remove disable skus from cart
    // disableCartSkus?.forEach((sku) => {
    //   const keyIndex = existingCartData?.data?.findIndex((data) => data?.id === sku);
    //   if (keyIndex >= 0) {
    //     const priceTobeRemoved = existingCartData?.data?.[keyIndex]?.price ?? 0;
    //     existingCartData?.data?.splice(keyIndex, 1);
    //     salesTotalExcludingVatAndTax = (salesTotalExcludingVatAndTax || 0) - priceTobeRemoved;
    //   }
    // });
  }

  //Update dynamic prices of selectables available in article.
  existingCartData?.data?.forEach((data) => {
    const updatedPrice = priceUpdate?.has(data?.id)
      ? Number(priceUpdate?.get(data?.id))
      : (selectablePrices?.[data?.id]?.priceExcludingVat ?? data?.price);

    if (updatedPrice !== data?.price) {
      salesTotalExcludingVatAndTax = salesTotalExcludingVatAndTax - data?.price + updatedPrice;
      data.price = updatedPrice;
    }
  });

  if (!isEqual(existingCartData?.data, cartData?.data)) {
    setCartData({
      ...cartData,
      data: existingCartData?.data,
      prices: {
        ...cartData?.prices,
        salesTotalExcludingVatAndTax,
      },
    });
  }
};

//Update dynamically changed prices of selectables in article
export const updateArticlePrices = (
  articles: INextArticle[],
  setArticles: (data: INextArticle[]) => void,
  disableArticles: string[],
  priceUpdate,
  selectablePrices: { string: {} } = null,
  selectableAlignSkuWithId: { id?: string; sku?: string }[] = []
) => {
  const existingArticles = cloneDeep(articles);
  if (disableArticles?.length) {
    //remove disable articles from cart
    // disableArticles?.forEach((sku) => {
    //   const keyIndex = existingArticles?.findIndex((article) => article?.id === sku);
    //   if (keyIndex >= 0) existingArticles?.splice(keyIndex, 1);
    // });
  }

  //Update dynamic prices of selectables available in article.
  existingArticles?.forEach((article) => {
    const articleSku = selectableAlignSkuWithId?.find((align) => align?.id === article?.id)?.sku;
    const priceExclVat = priceUpdate?.has(articleSku)
      ? Number(priceUpdate?.get(articleSku))
      : (selectablePrices?.[articleSku]?.priceExcludingVat ?? article?.priceExclVat);
    article.priceExclVat = priceExclVat;
    if (article?.articleType === ArticleType.EQUIPMENT) {
      const dealerPriceExclVat = calculateRetailPrice(priceExclVat, selectablePrices?.[articleSku]?.marginPer);
      article.dealerPriceExclVat = dealerPriceExclVat;
    }
  });
  if (!isEqual(existingArticles, articles)) {
    setArticles(existingArticles);
  }
};

export const prepareInitialEquipments = (order: IOrder, configuration: IConfiguration = null) => {
  const initialEquipmentCart = addInitialEquipment(order, order?.equipments ?? [], configuration);
  return { initialEquipmentCart };
};

export const getMarginDiscountValue = (margin: IMargin, order: IOrder): number => {
  // margin?.discountValue as null indicates that the user is yet to add discount.
  if (margin?.discountValue !== null) {
    return margin?.discountValue || 0;
  }

  return (
    order?.campaignSnapshot?.customerAdvantageTerms?.customerAdvantageAmount ||
    order?.pimSnapshot?.customerAdvantageExcludingVat ||
    0
  );
};

export const getOtherFeeTotal = (pimSnapshot: IPimSnapshot, overriddenTaxDetails?: IOverriddenTaxAndUsageCode) => {
  if (isEmpty(pimSnapshot)) return 0;
  if (overriddenTaxDetails?.overriddenVehicleRoadTax === 0) {
    return 0;
  }
  let scrapDeposit = defaultTo(pimSnapshot?.extraWreckage, 0);
  if (overriddenTaxDetails?.overriddenVehicleRoadTax >= DEFAULT_TAX.NON_ZERO && scrapDeposit === DEFAULT_TAX.ZERO) {
    scrapDeposit = DEFAULT_TAX.NON_ZERO;
  }
  const noxTax = defaultTo(pimSnapshot?.noxTax, 0);
  const co2Tax = defaultTo(pimSnapshot?.co2Tax, 0);
  return scrapDeposit + noxTax + co2Tax;
};

// Prepare initial cartData
export const prepareInitialCart = (
  order: IOrder,
  vehicle: IVehicleConfiguration,
  configuration: IConfiguration = null
) => {
  const isSkipPIM = isSkipCrystallize(configuration, order);
  const isEmptyOrder = order?.lead?.make === MAKE.Mercedes && isEmpty(order?.pimSnapshot);

  if (!order || (!isSkipPIM && (!vehicle || order?.orderReference?.pimCode !== vehicle?.sku)) || isEmptyOrder)
    return { initialCart: [] };

  let initialCart: CartSummaryData[] = [];
  const { pimSnapshot } = order;

  const modelCart = prepareVehicleModel(vehicle, pimSnapshot, order, isSkipPIM);

  const variantCart = prepareVehicleVariant(vehicle, pimSnapshot, order, isSkipPIM);

  initialCart = [modelCart, variantCart];

  if (order?.options?.length) {
    const option = order.options[0];
    const colorCart = prepareVehicleConfig(
      VehicleConfig.COLOR,
      option?.colorId,
      option?.colorName,
      option?.colorPriceExcludingVat,
      option?.colorVatCode,
      false,
      option?.colorRetailPrice
    );
    if (colorCart?.name || colorCart?.price) {
      initialCart?.push(colorCart);
    }
    const interiorCart = prepareVehicleConfig(
      VehicleConfig.INTERIOR,
      option?.interiorId,
      option?.interiorName,
      option?.interiorPriceExcludingVat,
      option?.interiorVatCode,
      false,
      option?.interiorRetailPrice
    );
    if (interiorCart?.name || interiorCart?.price) {
      initialCart?.push(interiorCart);
    }
  }

  const deliveryCharge = pimSnapshot?.extraDeliveryCharge || 0;
  const [cartInput] = prepareDeliveryChargeInput(
    deliveryCharge,
    findCorrectVatCode({
      usageCode: pimSnapshot?.vehicleUsageCode,
      articleType: ArticleType.DELIVERY,
      vatCode: pimSnapshot?.vatCode,
    })
  );
  initialCart = [...initialCart, cartInput as CartSummaryData];

  if (order?.equipments?.length) {
    initialCart = removeNullOrUndefindValuesFromArray(initialCart);
    const { initialEquipmentCart } = prepareInitialEquipments(order, configuration);
    initialCart = [...initialCart, ...initialEquipmentCart];
  }

  if (order?.accessories?.length) {
    initialCart = removeNullOrUndefindValuesFromArray(initialCart);
    const { initialCartAccessory } = addInitialAccessories(order, order?.accessories);
    initialCart = [...initialCart, ...initialCartAccessory];
  }

  if (order?.pCodes?.length) {
    initialCart = removeNullOrUndefindValuesFromArray(initialCart);
    const { initialCartPcodes } = addInitialPcodes(order?.pCodes);
    initialCart = [...initialCart, ...initialCartPcodes];
  }

  return { initialCart: removeNullOrUndefindValuesFromArray(initialCart) };
};

export const prepareInitialCartData = (
  variant: IModelPackages,
  model: IVehicleModel,
  order: IOrder,
  accTool: AccessoryTool,
  vehicleConfig: IVehicleConfiguration,
  configuration: IConfiguration = null
): CartSummaryData[] => {
  let initialCartData: CartSummaryData[] = [];
  if (!order || !variant || vehicleConfig?.sku !== variant?.sku || !accTool) {
    return [];
  }
  variant.isRecommended = variant?.price === model?.basePrice;
  variant.price = variant?.price > model?.basePrice ? (variant?.price || 0) - (model?.basePrice || 0) : 0;

  const currentModelData = prepareVehicleConfig(
    VehicleConfig.MODEL,
    model?.id,
    model?.name,
    model?.basePrice,
    model?.vatCode
  );

  const variantData = prepareVehicleConfig(
    VehicleConfig.VARIANT,
    variant?.id,
    variant?.name,
    variant?.price,
    variant?.vatCode,
    variant?.isRecommended
  );
  initialCartData = [currentModelData, variantData];

  if (order?.equipments?.length) {
    initialCartData = removeNullOrUndefindValuesFromArray(initialCartData);
    initialCartData = [...initialCartData, ...addInitialEquipment(order, order?.equipments, configuration)];
  }
  if (accTool?.tireSetData?.length) {
    const { initialTyre: tireSetConfigData } = addInitialVehicleTyre(accTool?.tireSetData);
    tireSetConfigData?.id && initialCartData.push(tireSetConfigData);
  }
  if (accTool?.accessoryData?.length) {
    initialCartData = removeNullOrUndefindValuesFromArray(initialCartData);
    const { initialCartAccessory } = addInitialAccessories(order, order?.accessories);
    initialCartData = [...initialCartData, ...initialCartAccessory];
  }

  return initialCartData;
};
